/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import moment from 'moment';
import debounce from 'lodash.debounce';
import { toast } from 'react-toastify';
import HireAgreementsService from '../../../../services/HireAgreements';
import EmployeeTitlesService from '../../../../services/EmployeeTitles';
import EmployeeDepartmentsService from '../../../../services/EmployeeDepartments';
import SchoolService from '../../../../services/School';
import PermissionGroupsService from '../../../../services/PermissionGroups';
import Pto from '../../../../services/Pto';
import EmployeeService from '../../../../services/Employee';
import UserService from '../../../../services/User';
import ManagementOfferLetter from '../ManagementOfferLetter';
import StaffOfferLetter from '../StaffOfferLetter';
import NewHireStepOne from './NewHireStepOne';
import NewHireStepTwo from './NewHireStepTwo';
import NewHireStepThree from './NewHireStepThree';
import NewHireStepFour from './NewHireStepFour';
import EmploymentDocumentsForm from '../EmploymentDocumentsForm';

interface NewHireModalContainerProps {
  isOpen: boolean;
  toggle: () => void;
  token: string;
  currentSchool: any;
  currentFranchisee: any;
  user: any;
  onSuccess: () => void;
  rehireUser: any;
  setRehireUser: (user: any) => void;
  confirmRehire: boolean;
  setConfirmRehire: (value: boolean) => void;
}

const validPermissionGroups = [
  { name: 'Accounting', label: 'Accounting' },
  { name: 'Director', label: 'Director' },
  { name: 'Staff', label: 'Staff' },
  { name: 'Teachers', label: 'Teacher' },
  { name: 'EducationDirectorAdminAssistant', label: 'Education Director / Admin Assistant' },
  { name: 'UpperManagement', label: 'Upper Management' },
  { name: 'HRUser', label: 'HR' },
  { name: 'FinanceUser', label: 'Finance' },
];

const NewHireModalContainer = ({
  isOpen,
  toggle,
  token,
  currentSchool,
  currentFranchisee,
  user,
  onSuccess,
  rehireUser,
  setRehireUser,
  confirmRehire,
  setConfirmRehire,
}: NewHireModalContainerProps) => {
  const [newEmployeeFormStep, setNewEmployeeFormStep] = useState(0);
  const [hireAgreementsList, setHireAgreementsList] = useState<any[]>([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [classification, setClassification] = useState('');
  const [hireDate, setHireDate] = useState(moment().format('MM/DD/yyyy'));
  const [isIncludeOfferLetter, setIsIncludeOfferLetter] = useState<boolean | null>();
  const [hiringAgreements, setHiringAgreements] = useState<any[]>([]);
  const [employmentEligibilityDocs, setEmploymentEligibilityDocs] = useState<string[]>(['I9_ENGLISH']);
  const [taxDocs, setTaxDocs] = useState<string[]>(['W4']);
  const [otherDocs, setOtherDocs] = useState<string[]>(['']);
  const [isAddToPayroll, setIsAddToPayroll] = useState<boolean | null>();
  const [isCollectTaxInformation, setIsCollectTaxInformation] = useState<boolean | null>();
  const [employees, setEmployees] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState<any[]>([{}]);
  const [offerLetter, setOfferLetter] = useState('');
  const [classroom, setClassroom] = useState('');
  const [education, setEducation] = useState('');
  const [experienceEce, setExperienceEce] = useState('');
  const [goodFitReason, setGoodFitReason] = useState('');
  const [ptoPolices, setPtoPolices] = useState<any[]>([]);
  const [titleList, setTitleList] = useState<any[]>([{}]);
  const [titleId, setTitleId] = useState('');
  const [groupsList, setGroupsList] = useState<{ id: string; name: string }[]>([]);
  const [groupId, setGroupId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState<string>();
  const [amount, setAmount] = useState<string>();
  const [policy, setPolicy] = useState<string>();
  const [isSign, setIsSign] = useState(false);
  const [signature, setSignature] = useState('');
  const [school, setSchool] = useState<any>();
  const [i9Option, setI9Option] = useState('I9_ENGLISH');
  const [isEmailExists, setIsEmailExists] = useState(false);

  const fetchModalDependencies = async () => {
    if (!token || !currentSchool?.value) return;
    try {
      const [
        hireAgreementsResponse,
        staffResponse,
        departmentsResponse,
        titlesResponse,
        ptoResponse,
        groupsResponse,
        schoolResponse,
      ] = await Promise.all([
        HireAgreementsService(token).list(),
        EmployeeService(token).listSelectStaff(currentSchool.value),
        EmployeeDepartmentsService(token).list(),
        EmployeeTitlesService(token).list(),
        Pto(token).ptoPolicyList(),
        PermissionGroupsService(token).list(),
        SchoolService(token).show(currentSchool.value),
      ]);

      setHireAgreementsList(hireAgreementsResponse.data || []);
      setEmployees(staffResponse?.data || []);
      setDepartmentList(departmentsResponse.data || []);
      setTitleList(titlesResponse.data || []);
      setPtoPolices(ptoResponse.data || []);

      const valids = validPermissionGroups.map((v) => v.name);
      const filteredGroups = (groupsResponse || []).filter((group: any) => valids.includes(group.name));
      setGroupsList(
        filteredGroups.map((group: any) => {
          const valid = validPermissionGroups.find((v) => v.name === group.name);
          return { id: group?.id || '', name: valid?.label || '' };
        }),
      );

      setSchool(schoolResponse?.data || {});
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load new hire data');
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchModalDependencies();
    }
  }, [isOpen, currentSchool, currentFranchisee]);

  const debouncedVerifyEmail = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          const emailExists = await UserService(token).getByEmail(value);
          setIsEmailExists(!!emailExists?.id);
        } catch (error) {
          console.error(error);
        }
      }, 800),
    [token],
  );

  useEffect(() => {
    if (email) {
      debouncedVerifyEmail(email);
    } else {
      setIsEmailExists(false);
    }

    return () => {
      debouncedVerifyEmail.cancel();
    };
  }, [email, debouncedVerifyEmail]);

  const changeEmploymentEligibility = (doc: string) => {
    const isChecked = employmentEligibilityDocs.includes(doc);
    const updated = isChecked
      ? employmentEligibilityDocs.filter((value) => value !== doc)
      : [...employmentEligibilityDocs, doc];
    setEmploymentEligibilityDocs(updated);
  };

  const changeOtherDocs = (doc: string) => {
    const isChecked = otherDocs.includes(doc);
    const updated = isChecked ? otherDocs.filter((value) => value !== doc) : [...otherDocs, doc];
    setOtherDocs(updated);
  };

  const changeTaxDocs = (doc: string) => {
    const isChecked = taxDocs.includes(doc);
    const updated = isChecked ? taxDocs.filter((value) => value !== doc) : [...taxDocs, doc];
    setTaxDocs(updated);
  };

  const selectAllDocs = () => {
    setEmploymentEligibilityDocs(['TWO_DOCS_FOR_PROOF']);
    setTaxDocs(['W4']);
    setHiringAgreements(hireAgreementsList.map((agreement) => agreement?.id));
    setOtherDocs(['EMPLOYEE-HANDBOOK']);
  };

  const resetFormState = () => {
    setNewEmployeeFormStep(0);
    setFirstName('');
    setLastName('');
    setEmail('');
    setConfirmEmail('');
    setPhone('');
    setClassification('');
    setHireDate(moment().format('MM/DD/yyyy'));
    setIsIncludeOfferLetter(null);
    setHiringAgreements([]);
    setEmploymentEligibilityDocs(['I9_ENGLISH']);
    setTaxDocs(['W4']);
    setIsAddToPayroll(null);
    setIsCollectTaxInformation(null);
    setOfferLetter('');
    setClassroom('');
    setEducation('');
    setExperienceEce('');
    setGoodFitReason('');
    setTitleId('');
    setGroupId('');
    setDepartmentId('');
    setSupervisorId('');
    setPaymentFrequency(undefined);
    setAmount('');
    setPolicy('');
    setSignature('');
    setIsSign(false);
    setIsEmailExists(false);
    setOtherDocs(['']);
    setI9Option('I9_ENGLISH');
  };

  const handleFindTitleName = () => {
    const titleName: any = titleList.find((value: any) => value.id === titleId);
    return titleName?.name || '';
  };

  const handleFindSupervisorName = () => {
    const supervisorName: any = employees.find((value: any) => value.userId === supervisorId);
    const supervisorFirstName = supervisorName?.firstName || supervisorName?.user?.firstName || '';
    const supervisorLastName = supervisorName?.lastName || supervisorName?.user?.lastName || '';
    return `${supervisorFirstName} ${supervisorLastName}`;
  };

  const handleSubmitForm = async (event: React.FormEvent) => {
    event?.preventDefault();

    if (newEmployeeFormStep === 0 && email !== confirmEmail) {
      toast.error('Email must be equal to confirm email');
      return;
    }

    if (newEmployeeFormStep !== 5) {
      setNewEmployeeFormStep((prev) => prev + 1);
      return;
    }

    if (!isSign || !signature) {
      toast.error('Your signature is required!');
      return;
    }

    const payload: any = {
      employeeId: rehireUser?.id || undefined,
      firstName,
      lastName,
      email,
      phone,
      schoolId: currentSchool.value,
      franchiseeId: currentFranchisee.value,
      titleId,
      groupId,
      departmentId,
      supervisorId,
      offerLetter,
      classroom,
      education,
      experienceEce,
      goodFitReason,
      ptoPolicyId: policy,
      startDate: hireDate,
      workday: classification,
      paymentFrequency,
      payRate: String(amount || '')
        .replace('$', '')
        .replace(',', ''),
      hiringAgreements,
      employmentEligibilityDocs: [...employmentEligibilityDocs, i9Option],
      taxDocs,
      otherDocs,
    };

    if (classification === 'FT' && paymentFrequency === 'hourly') {
      payload.hoursPerWeek = 40;
    }

    if (classification === 'PT' && paymentFrequency === 'hourly') {
      payload.hoursPerWeek = 32;
    }

    if (classification === 'FT' && paymentFrequency === 'annually') {
      payload.hoursPerWeek = 45;
    }

    if (classification === 'Sub') {
      payload.hoursPerWeek = 40;
    }

    if (isIncludeOfferLetter) {
      payload.imageUrl = school?.franchisee?.imageUrl;
      payload.senderName = `${user.firstName} ${user.lastName}`;
      payload.senderPhone = user?.phone || '';
      payload.currentSchool = currentSchool.label;
      payload.positionName = handleFindTitleName();
      payload.signature = signature;
      payload.supervisorName = handleFindSupervisorName();
    }

    try {
      await EmployeeService(token).createNewHire(payload);
      toast.success('Saved successfully!');
      resetFormState();
      setConfirmRehire(false);
      setRehireUser({});
      toggle();
      onSuccess();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Sorry, error on save.');
    }
  };

  const loadRehire = () => {
    if (!rehireUser) return;
    setNewEmployeeFormStep(0);
    setFirstName(rehireUser?.user?.firstName || '');
    setLastName(rehireUser?.user?.lastName || '');
    setEmail(rehireUser?.user?.email || '');
    setConfirmEmail(rehireUser?.user?.email || '');
    setPhone(rehireUser?.user?.phone || '');
    setClassification(rehireUser?.workday || '');
    setOfferLetter(rehireUser?.offerLetter || '');
    setClassroom(rehireUser?.classroom || '');
    setEducation(rehireUser?.education || '');
    setExperienceEce(rehireUser?.experienceEce || '');
    setGoodFitReason(rehireUser?.goodFitReason || '');
    setIsIncludeOfferLetter(rehireUser?.offerLetter ? true : null);
    setHireDate(moment().format('MM/DD/yyyy'));
    setHiringAgreements([]);
    setIsAddToPayroll(null);
    setIsCollectTaxInformation(null);
    setTitleId(rehireUser?.titleId || '');
    setGroupId(rehireUser?.user?.groupId || '');
    setDepartmentId(rehireUser?.departmentId || '');
    setSupervisorId(rehireUser?.supervisorId || '');
    setPaymentFrequency(rehireUser?.paymentFrequency || '');
    setAmount(rehireUser?.payRate || '');
    setPolicy(rehireUser?.ptoPolicyId || '');
    if (!isOpen) {
      toggle();
    }
  };

  useEffect(() => {
    if (confirmRehire) {
      loadRehire();
      setConfirmRehire(false);
    }
  }, [confirmRehire]);

  useEffect(() => {
    if (!isOpen) {
      resetFormState();
    }
  }, [isOpen]);

  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={toggle} size="xl">
      <div className="modal-header">
        <h4 className="modal-title">Add a New Hire</h4>
        <div onClick={toggle} className="d-flex p-1 cursor-pointer">
          <i className="fas fa-times" />
        </div>
      </div>
      <div className="modal-body">
        <div className="px-2">
          {newEmployeeFormStep === 0 && (
            <NewHireStepOne
              {...{
                handleSubmitForm,
                firstName,
                setFirstName,
                lastName,
                setLastName,
                email,
                setEmail,
                isEmailExists,
                confirmEmail,
                setConfirmEmail,
                phone,
                setPhone,
                groupId,
                setGroupId,
                groupsList,
                classification,
                setClassification,
                paymentFrequency,
                setPaymentFrequency,
                amount,
                setAmount,
                policy,
                setPolicy,
                ptoPolices,
              }}
            />
          )}

          {newEmployeeFormStep === 1 && (
            <NewHireStepTwo
              {...{
                currentUser: user,
                handleSubmitForm,
                currentSchool,
                setTitleId,
                titleId,
                titleList,
                setHireDate,
                hireDate,
                setDepartmentId,
                departmentId,
                departmentList,
                setSupervisorId,
                supervisorId,
                employees,
                isIncludeOfferLetter,
                setIsIncludeOfferLetter,
                offerLetter,
                setOfferLetter,
                setNewEmployeeFormStep,
              }}
            />
          )}

          {newEmployeeFormStep === 2 && (
            <NewHireStepThree
              {...{
                handleSubmitForm,
                classroom,
                setClassroom,
                education,
                setEducation,
                experienceEce,
                setExperienceEce,
                goodFitReason,
                setGoodFitReason,
                setNewEmployeeFormStep,
              }}
            />
          )}

          {newEmployeeFormStep === 3 && (
            <EmploymentDocumentsForm
              hireAgreementsList={hireAgreementsList}
              hiringAgreements={hiringAgreements}
              taxDocs={taxDocs}
              employmentEligibilityDocs={employmentEligibilityDocs}
              changeEmploymentEligibility={changeEmploymentEligibility}
              changeTaxDocs={changeTaxDocs}
              handleSubmitForm={handleSubmitForm}
              setHiringAgreements={setHiringAgreements}
              setNewEmployeeFormStep={setNewEmployeeFormStep}
              selectAllDocs={selectAllDocs}
              setI9Option={setI9Option}
              i9Option={i9Option}
              changeOtherDocs={changeOtherDocs}
              otherDocs={otherDocs}
            />
          )}

          {newEmployeeFormStep === 4 && (
            <NewHireStepFour
              {...{
                handleSubmitForm,
                isAddToPayroll,
                setIsAddToPayroll,
                isCollectTaxInformation,
                setIsCollectTaxInformation,
                setNewEmployeeFormStep,
              }}
            />
          )}

          {newEmployeeFormStep === 5 && (
            <Form onSubmit={handleSubmitForm}>
              <Row className="d-flex aligm-items-center mt-4">
                <h2>
                  Review {firstName} {lastName} offer letter.
                </h2>
              </Row>

              <hr />
              {offerLetter === 'Management' ? (
                <ManagementOfferLetter
                  school={school}
                  user={user}
                  email={email}
                  phone={phone}
                  firstName={firstName}
                  lastName={lastName}
                  hireDate={hireDate}
                  classification={classification}
                  currentSchool={currentSchool}
                  paymentFrequency={paymentFrequency || ''}
                  amount={amount}
                  signature={signature}
                  policy={policy}
                  ptoPolices={ptoPolices}
                  handleFindTitleName={handleFindTitleName}
                  handleFindSupervisorName={handleFindSupervisorName}
                />
              ) : (
                <></>
              )}
              {offerLetter === 'Staff' ? (
                <StaffOfferLetter
                  school={school}
                  user={user}
                  email={email}
                  phone={phone}
                  firstName={firstName}
                  lastName={lastName}
                  hireDate={hireDate}
                  classification={classification}
                  paymentFrequency={paymentFrequency || ''}
                  amount={amount}
                  signature={signature}
                  handleFindTitleName={handleFindTitleName}
                  handleFindSupervisorName={handleFindSupervisorName}
                />
              ) : (
                <></>
              )}
              {!offerLetter ? <span>Offer Letter not included.</span> : <></>}
              <Row>
                <Col lg="12" className="mt-4">
                  <Label>
                    <strong>Signature:</strong>
                  </Label>
                  <div className="d-flex flex-wrap align-items-center">
                    <div className="form-check form-check-inline mr-3">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id="to-sign"
                        checked={isSign}
                        onChange={() => {
                          if (isSign) {
                            setSignature('');
                          }
                          setIsSign(!isSign);
                        }}
                      />
                      <Label className="form-check-label mr-2" htmlFor="to-sign">
                        I hereby acknowledge and agree that my electronic signature on this document is the legal
                        equivalent of my handwritten signature.
                      </Label>
                    </div>
                  </div>
                </Col>

                {isSign && (
                  <Col lg="12" className="mt-4">
                    <FormGroup>
                      <Label htmlFor="name-for-signature">
                        <strong>Enter your name for signature:</strong>
                      </Label>
                      <Input
                        className="form-control"
                        type="text"
                        id="name-for-signature"
                        style={{ fontFamily: 'Dancing Script' }}
                        value={signature}
                        onChange={(e) => {
                          setSignature(e.target.value);
                        }}
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>

              <div className="d-flex justify-content-end mt-4">
                <div>
                  <Button className="btn-secondary" onClick={() => setNewEmployeeFormStep(3)}>
                    Back
                  </Button>
                  <Button color="warning" type="submit">
                    Submit
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default NewHireModalContainer;
